// Photo filter definitions — shared across web and mobile
// Each filter is a 4×5 color matrix (20 numbers) that transforms RGBA pixels:
//
//   [R']   [a0  a1  a2  a3  a4 ] [R]
//   [G'] = [a5  a6  a7  a8  a9 ] [G]
//   [B']   [a10 a11 a12 a13 a14] [B]
//   [A']   [a15 a16 a17 a18 a19] [A, 1 (offset)]
//
// The 5th column (a4, a9, a14, a19) is the offset/bias added after multiplication.
// Values are in 0–255 space for offsets, 0–1 for multipliers.

export interface PhotoFilter {
  id: string;
  name: string;
  matrix: number[];
}

// Identity matrix — no transformation
export const IDENTITY_MATRIX: number[] = [
  1, 0, 0, 0, 0,
  0, 1, 0, 0, 0,
  0, 0, 1, 0, 0,
  0, 0, 0, 1, 0,
];

// --- Matrix composition helpers ---

/**
 * Multiply two 4×5 color matrices. Result = applying B then A.
 * Treats each as a 4×5 augmented matrix (4×4 linear + offset column).
 */
export function concatColorMatrices(a: number[], b: number[]): number[] {
  const result: number[] = new Array(20);
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 5; col++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) {
        sum += a[row * 5 + k] * b[k * 5 + col];
      }
      // Add offset column contribution
      if (col === 4) {
        sum += a[row * 5 + 4];
      }
      result[row * 5 + col] = sum;
    }
  }
  return result;
}

/** Chain multiple matrices left-to-right (first applied first). */
function chain(...matrices: number[][]): number[] {
  return matrices.reduce((acc, m) => concatColorMatrices(m, acc));
}

// --- Primitive transform matrices ---

export function brightnessMatrix(amount: number): number[] {
  // amount: offset in 0–1 space (e.g. 0.05 = +5% brightness)
  const b = amount * 255;
  return [
    1, 0, 0, 0, b,
    0, 1, 0, 0, b,
    0, 0, 1, 0, b,
    0, 0, 0, 1, 0,
  ];
}

export function contrastMatrix(amount: number): number[] {
  // amount: 1 = no change, >1 = more contrast, <1 = less
  const t = (1 - amount) * 127.5;
  return [
    amount, 0, 0, 0, t,
    0, amount, 0, 0, t,
    0, 0, amount, 0, t,
    0, 0, 0, 1, 0,
  ];
}

export function saturateMatrix(amount: number): number[] {
  // amount: 1 = no change, 0 = grayscale, >1 = oversaturated
  const s = amount;
  const sr = (1 - s) * 0.2126;
  const sg = (1 - s) * 0.7152;
  const sb = (1 - s) * 0.0722;
  return [
    sr + s, sg, sb, 0, 0,
    sr, sg + s, sb, 0, 0,
    sr, sg, sb + s, 0, 0,
    0, 0, 0, 1, 0,
  ];
}

export function grayscaleMatrix(amount: number): number[] {
  // amount: 0 = no change, 1 = full grayscale
  return saturateMatrix(1 - amount);
}

export function sepiaMatrix(amount: number): number[] {
  // amount: 0 = no change, 1 = full sepia
  const s = 1 - amount;
  return [
    s + amount * 0.393, amount * 0.769, amount * 0.189, 0, 0,
    amount * 0.349, s + amount * 0.686, amount * 0.168, 0, 0,
    amount * 0.272, amount * 0.534, s + amount * 0.131, 0, 0,
    0, 0, 0, 1, 0,
  ];
}

export function tintMatrix(r: number, g: number, b: number, amount: number): number[] {
  // Add a color tint. r/g/b in 0–1, amount controls strength.
  return [
    1, 0, 0, 0, r * amount * 255,
    0, 1, 0, 0, g * amount * 255,
    0, 0, 1, 0, b * amount * 255,
    0, 0, 0, 1, 0,
  ];
}

// --- Filter presets (garage-themed names) ---

export const PHOTO_FILTERS: PhotoFilter[] = [
  {
    id: "original",
    name: "Original",
    matrix: IDENTITY_MATRIX,
  },
  {
    id: "chrome",
    name: "Chrome",
    // Boosted contrast + saturation, cool shadows
    matrix: chain(
      contrastMatrix(1.2),
      saturateMatrix(1.3),
      tintMatrix(0, 0.05, 0.1, 0.15),
    ),
  },
  {
    id: "patina",
    name: "Patina",
    // Soft, warm, low contrast
    matrix: chain(
      brightnessMatrix(0.05),
      contrastMatrix(0.9),
      tintMatrix(0.1, 0.05, 0, 0.12),
    ),
  },
  {
    id: "moonlight",
    name: "Moonlight",
    // Desaturated, cool, high contrast
    matrix: chain(
      grayscaleMatrix(0.8),
      contrastMatrix(1.1),
      tintMatrix(0, 0.02, 0.08, 0.2),
    ),
  },
  {
    id: "daybreak",
    name: "Daybreak",
    // Bright, desaturated reds, boosted blues — custom channel mix
    matrix: [
      0.9, 0.1, 0, 0, 10,
      0, 1.0, 0.05, 0, 5,
      0, 0.05, 1.15, 0, 15,
      0, 0, 0, 1, 0,
    ],
  },
  {
    id: "vintage",
    name: "Vintage",
    // Low contrast, sepia tint
    matrix: chain(
      contrastMatrix(0.85),
      sepiaMatrix(0.3),
      brightnessMatrix(0.1),
    ),
  },
  {
    id: "burnout",
    name: "Burnout",
    // Warm tint, boosted reds/yellows
    matrix: chain(
      saturateMatrix(1.2),
      tintMatrix(0.12, 0.06, 0, 0.15),
    ),
  },
  {
    id: "dusk",
    name: "Dusk",
    // Desaturated, warm, dreamy
    matrix: chain(
      saturateMatrix(0.7),
      contrastMatrix(0.9),
      tintMatrix(0.1, 0.05, 0.02, 0.15),
    ),
  },
  {
    id: "overcast",
    name: "Overcast",
    // Pastel, desaturated, warm
    matrix: chain(
      saturateMatrix(0.8),
      brightnessMatrix(0.08),
      tintMatrix(0.08, 0.04, 0, 0.12),
    ),
  },
  {
    id: "teal-drive",
    name: "Teal Drive",
    // Cool teal shadows, warm highlights — custom channel mix
    matrix: [
      1.05, 0, 0, 0, -5,
      0, 1.05, 0.08, 0, 5,
      0, 0.05, 1.12, 0, 12,
      0, 0, 0, 1, 0,
    ],
  },
  {
    id: "blackout",
    name: "Blackout",
    // Pure black & white
    matrix: grayscaleMatrix(1),
  },
  {
    id: "neon",
    name: "Neon",
    // Warm pink/magenta tint, high contrast
    matrix: chain(
      contrastMatrix(1.2),
      tintMatrix(0.12, 0, 0.06, 0.18),
    ),
  },
];
